import {
  Box,
  Button,
  Center,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Icon,
  Input,
  Stack,
  Textarea,
  Text,
} from "@chakra-ui/react";
import { useWebApi } from "./useWebApi";
import { FaCode } from "react-icons/fa6";
import { Editor } from "@monaco-editor/react";
import { MdEditOff, MdModeEdit, MdSave } from "react-icons/md";
import Split from "react-split";
import { Table } from "../../components/Table/Table";
import { BrandButton } from "../../components/BrandButton/BrandButton";
import { ChevronDownIcon, ChevronUpIcon, DeleteIcon } from "@chakra-ui/icons";
import { DropDown } from "../../components/Select/DropDown";

export const WebApi = () => {
  const {
    selectedWebApi,
    isEditing,
    onEditToggle,
    isEdited,
    selectedWebApiBody,
    editorOptions,
    selectedMethod,
    setSelectedMethod,
    methods,
    selectedContentType,
    setSelectedContentType,
    contentTypes,
    body,
    setBody,
    requestBody,
    setRequestBody,
    requestBodyMarker,
    setRequestBodyMarker,
    headers,
    setHeaders,
    queryParams,
    setQueryParams,
    onValueChange,
    handleAddHeader,
    handleRemoveHeader,
    handleHeaderChange,
    moveHeader,
    handleAddQueryParam,
    handleRemoveQueryParam,
    handleQueryParamChange,
    moveQueryParam,
    onTest,
    onSave,
    isTesting,
    isSaving,
    response,
  } = useWebApi();

  return (
    <Box
      bg="rgba(255, 255, 255, 0.05)"
      backdropFilter="blur(20px)"
      borderRadius="20px"
      border="1px solid rgba(255, 255, 255, 0.1)"
      p={6}
    >
      <Box borderRadius="md" py={3}>
        <HStack justifyContent="space-between">
          <HStack>
            <Icon
              as={FaCode as React.ElementType}
              color="orange.400"
              boxSize="24px"
            />
            <Heading fontSize="24px" color="white">
              {selectedWebApi?.apiNm}
            </Heading>
          </HStack>
          <HStack>
            <Icon
              as={(isEditing ? MdEditOff : MdModeEdit) as React.ElementType}
              onClick={onEditToggle}
              cursor="pointer"
              boxSize="20px"
              color="rgba(255, 255, 255, 0.8)"
              _hover={{ color: "brand.400" }}
            />
            {isEdited ? (
              <Button
                boxSize="20px"
                cursor="pointer"
                variant="unstyled"
                isLoading={isSaving}
                onClick={onSave}
              >
                <Icon as={MdSave as React.ElementType} boxSize="20px" />
              </Button>
            ) : null}
          </HStack>
        </HStack>
      </Box>

      <Box
        as={Split}
        display="flex"
        flexDir="row"
        gutterStyle={() => ({
          backgroundColor: "rgba(255, 255, 255, 0.2)",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "50%",
          cursor: "col-resize",
          width: "4px",
          borderRadius: "2px",
        })}
        gap={3}
      >
        <Box width="40%" borderRadius="md">
          <HStack justifyContent="space-between" p={3}>
            <Heading fontSize="22px" color="white">
              Input body
            </Heading>
          </HStack>
          <Editor
            value={body}
            defaultLanguage="groovy"
            height="50vh"
            theme="vs-dark"
            options={editorOptions}
            onChange={(e) => setBody(e || "")}
            onMount={(editor, monaco) => {
              const container = editor.getContainerDomNode();

              // Use ResizeObserver to handle layout updates
              const resizeObserver = new ResizeObserver(() => {
                editor.layout();
              });

              resizeObserver.observe(container);

              // Cleanup observer on unmount
              return () => {
                resizeObserver.disconnect();
              };
            }}
          />

          {response && (
            <Box
              bg="rgba(0, 0, 0, 0.3)"
              borderRadius="12px"
              border="1px solid rgba(255, 255, 255, 0.1)"
              mt={3}
              p={4}
            >
              <Text
                color="green.300"
                fontFamily="mono"
                fontSize="sm"
                whiteSpace="pre-wrap"
              >
                {typeof response === "string"
                  ? response
                  : JSON.stringify(response, null, 2)}
              </Text>
            </Box>
          )}
        </Box>
        <Box width="60%">
          <HStack justifyContent="space-between" p={3}>
            <Heading fontSize="22px" color="white">
              Test inputs
            </Heading>
          </HStack>
          <Box
            bg="rgba(255, 255, 255, 0.05)"
            backdropFilter="blur(20px)"
            borderRadius="20px"
            border="1px solid rgba(255, 255, 255, 0.1)"
            p={6}
          >
            <FormControl id="methods" mb={4}>
              <FormLabel color="white">Method</FormLabel>
              <DropDown
                selected={{
                  id: selectedMethod as string,
                  name: selectedMethod as string,
                  component: <Text>{selectedMethod}</Text>,
                }}
                title={<Text></Text>}
                options={methods.map((method, index) => ({
                  id: method,
                  name: method,
                  component: <Text key={index}>{method}</Text>,
                }))}
                onOptionChange={(option) => {
                  if (!Array.isArray(option)) {
                    setSelectedMethod(option.id);
                  }
                }}
                disabled={!isEditing}
              />
            </FormControl>

            {selectedMethod !== "GET" && (
              <FormControl id="content-type" mb={4}>
                <FormLabel color="white">Content type</FormLabel>
                <DropDown
                  selected={{
                    id: selectedContentType as string,
                    name: selectedContentType as string,
                    component: <Text>{selectedContentType}</Text>,
                  }}
                  title={<Text></Text>}
                  options={contentTypes.map((type, index) => ({
                    id: type,
                    name: type,
                    component: <Text key={index}>{type}</Text>,
                  }))}
                  onOptionChange={(option) => {
                    if (!Array.isArray(option)) {
                      setSelectedContentType(option.id);
                    }
                  }}
                  disabled
                />
              </FormControl>
            )}

            <FormControl id="name" mb={4}>
              <FormLabel color="white">Name</FormLabel>
              <Input
                placeholder="Enter Web API name"
                focusBorderColor="brand.500"
                value={selectedWebApi?.apiNm}
                onChange={(e) => onValueChange("apiNm", e.target.value)}
                isReadOnly={!isEditing}
              />
            </FormControl>

            <FormControl id="description" mb={4}>
              <FormLabel color="white">Description</FormLabel>
              <Textarea
                placeholder="Enter Web API description"
                focusBorderColor="brand.500"
                value={selectedWebApi?.apiDesc}
                onChange={(e) => onValueChange("apiDesc", e.target.value)}
                isReadOnly={!isEditing}
              />
            </FormControl>

            <FormControl id="relativePath" mb={4}>
              <FormLabel color="white">Relative Path</FormLabel>
              <Input
                placeholder="Enter relative path"
                focusBorderColor="brand.500"
                value={selectedWebApi?.relativePath}
                onChange={(e) => onValueChange("relativePath", e.target.value)}
                isReadOnly={!isEditing}
              />
            </FormControl>

            <FormControl id="absolutePath" mb={4}>
              <FormLabel color="white">Absolute Path</FormLabel>
              <Input
                placeholder="Enter relative path"
                focusBorderColor="brand.500"
                value={`${process.env.REACT_APP_BACKEND_API}/main/webapi/${selectedWebApi?.relativePath}`}
                isReadOnly={true}
              />
            </FormControl>

            <Flex width="100%" gap={2}>
              {selectedMethod !== "GET" && (
                <Box width="50%">
                  <FormControl id="body" mb={4}>
                    <FormLabel color="white">Body</FormLabel>
                    <Box
                      border="1px"
                      borderColor="gray.200"
                      borderRadius="md"
                      p={2}
                    >
                      <Editor
                        value={requestBody}
                        defaultLanguage="json"
                        theme="vs-dark"
                        height="25vh"
                        width="100%"
                        options={editorOptions}
                        onChange={(e) => {
                          if (e) setRequestBody(e);
                        }}
                        onValidate={(marker) => setRequestBodyMarker(marker)}
                      />
                    </Box>
                  </FormControl>
                </Box>
              )}
              <Box width={selectedMethod !== "GET" ? "50%" : "100%"}>
                <FormControl id="headers" mb={4}>
                  <FormLabel color="white">Headers</FormLabel>
                  <Stack spacing={3}>
                    {headers
                      .filter((header) => header.isActive === "Y")
                      .map((header, index) => (
                        <HStack key={header.id} spacing={3}>
                          <Input
                            placeholder="Key"
                            value={header.headNm}
                            onChange={(e) =>
                              handleHeaderChange(
                                header.id,
                                "headNm",
                                e.target.value
                              )
                            }
                            focusBorderColor="brand.500"
                            isReadOnly={!isEditing}
                          />

                          <Center
                            width="100%"
                            height="40px"
                            border="1px solid"
                            borderColor="gray.300" // Chakra UI border color
                            borderRadius="md" // Chakra UI rounded corners
                          >
                            <Editor
                              height="30px" // Fixed height for one line
                              defaultLanguage="python"
                              defaultValue="Value"
                              value={header.headVal}
                              theme="vs-dark"
                              onChange={(e) => {
                                if (e)
                                  handleHeaderChange(header.id, "headVal", e);
                              }}
                              options={{
                                lineNumbers: "off",
                                minimap: { enabled: false },
                                scrollbar: {
                                  vertical: "hidden",
                                  horizontal: "hidden",
                                },
                                wordWrap: "off",
                                scrollBeyondLastLine: false,
                                overviewRulerLanes: 0,
                                hideCursorInOverviewRuler: true,
                                folding: false,
                                renderLineHighlight: "none",
                                tabCompletion: "off",
                                lineDecorationsWidth: 0,
                                overviewRulerBorder: false,
                                cursorStyle: "line", // Standard cursor style
                                fontSize: 14, // Font size similar to input
                                readOnly: !isEditing,
                              }}
                            />
                          </Center>

                          <Button
                            size="sm"
                            onClick={() => handleRemoveHeader(header.id)}
                            isDisabled={!isEditing}
                          >
                            <DeleteIcon />
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => moveHeader(index, "up")}
                            isDisabled={!isEditing || index === 0}
                          >
                            <ChevronUpIcon />
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => moveHeader(index, "down")}
                            isDisabled={
                              !isEditing || index === headers.length - 1
                            }
                          >
                            <ChevronDownIcon />
                          </Button>
                        </HStack>
                      ))}
                    <BrandButton
                      size="md"
                      colorScheme="brand"
                      variant="solid"
                      onClick={handleAddHeader}
                      isDisabled={!isEditing}
                    >
                      Add Header
                    </BrandButton>
                  </Stack>
                </FormControl>

                <FormControl id="queryParams" mb={4}>
                  <FormLabel color="white">Query Parameters</FormLabel>
                  <Stack spacing={3}>
                    {queryParams
                      .filter((query) => query.isActive === "Y")
                      .map((query, index) => (
                        <HStack key={query.id} spacing={3}>
                          <Input
                            placeholder="Key"
                            value={query.paramNm}
                            onChange={(e) =>
                              handleQueryParamChange(
                                query.id,
                                "paramNm",
                                e.target.value
                              )
                            }
                            focusBorderColor="brand.500"
                            isReadOnly={!isEditing}
                          />

                          <Center
                            width="100%"
                            height="40px"
                            border="1px solid"
                            borderColor="gray.300" // Chakra UI border color
                            borderRadius="md" // Chakra UI rounded corners
                          >
                            <Editor
                              height="30px" // Fixed height for one line
                              defaultLanguage="python"
                              defaultValue="Value"
                              value={query.paramVal}
                              theme="vs-dark"
                              onChange={(e) => {
                                if (e)
                                  handleQueryParamChange(
                                    query.id,
                                    "paramVal",
                                    e
                                  );
                              }}
                              options={{
                                lineNumbers: "off",
                                minimap: { enabled: false },
                                scrollbar: {
                                  vertical: "hidden",
                                  horizontal: "hidden",
                                },
                                wordWrap: "off",
                                scrollBeyondLastLine: false,
                                overviewRulerLanes: 0,
                                hideCursorInOverviewRuler: true,
                                folding: false,
                                renderLineHighlight: "none",
                                tabCompletion: "off",
                                lineDecorationsWidth: 0,
                                overviewRulerBorder: false,
                                cursorStyle: "line", // Standard cursor style
                                fontSize: 14, // Font size similar to input
                                readOnly: !isEditing,
                              }}
                            />
                          </Center>

                          <Button
                            size="sm"
                            onClick={() => handleRemoveQueryParam(query.id)}
                            isDisabled={!isEditing}
                          >
                            <DeleteIcon />
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => moveQueryParam(index, "up")}
                            isDisabled={!isEditing || index === 0}
                          >
                            <ChevronUpIcon />
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => moveQueryParam(index, "down")}
                            isDisabled={
                              !isEditing || index === queryParams.length - 1
                            }
                          >
                            <ChevronDownIcon />
                          </Button>
                        </HStack>
                      ))}
                    <BrandButton
                      size="md"
                      colorScheme="brand"
                      variant="solid"
                      onClick={handleAddQueryParam}
                      isDisabled={!isEditing}
                    >
                      Add Query Parameter
                    </BrandButton>
                  </Stack>
                </FormControl>
              </Box>
            </Flex>
            <Flex justifyContent="flex-end">
              <BrandButton
                // size="sm"
                onClick={() => {
                  onTest();
                }}
                isLoading={isTesting}
                mt={4}
              >
                Test
              </BrandButton>
            </Flex>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
